import { Button, Select, TextField } from '@cimpress/react-components';
import IconRemove from '@cimpress-technology/react-streamline-icons/lib/IconRemove';
import { useState } from 'react';
import { isStringEmptyOrWhitespace, useUpdateEffect } from '../../tools';
import type { SelectorOption } from '../common';
import { Control } from '../layout';

export interface TextAsset {
    content?: string;
    purpose?: string;
}

const TEXT_PURPOSE_SELECTOR_OPTIONS: SelectorOption[] = [
    { label: 'None', value: '' },
    { label: 'Company Name', value: 'companyname' },
    { label: 'Tagline', value: 'tagline' },
    { label: 'Website', value: 'website' },
    { label: 'Email', value: 'email' },
    { label: 'Phone', value: 'phone' },
];

export interface StylizedTextAssetEditorProps {
    assetId: string;
    initialValue?: TextAsset;
    onChange: (assetId: string, value: TextAsset) => void;
    onRemove: (assetId: string) => void;
}

interface TextState {
    content: string;
    purpose: string;
}

function assetToState(textAsset?: TextAsset): TextState {
    if (!textAsset) {
        return {
            content: '',
            purpose: '',
        };
    }

    const { content, purpose } = textAsset;

    return {
        content: content ?? '',
        purpose: purpose ?? '',
    };
}

export const StylizedTextAssetEditor = ({
    assetId,
    initialValue,
    onChange,
    onRemove,
}: StylizedTextAssetEditorProps) => {
    const [text, setText] = useState(() => assetToState(initialValue));
    const [isContentDirty, setIsContentDirty] = useState(false);
    const [purposeSelector, setPurposeSelector] = useState<SelectorOption>(
        TEXT_PURPOSE_SELECTOR_OPTIONS.find((p) => p.value === initialValue?.purpose) ??
            TEXT_PURPOSE_SELECTOR_OPTIONS[0],
    );

    const { content, purpose } = text;

    useUpdateEffect(() => {
        if (!isStringEmptyOrWhitespace(content)) {
            const asset: TextAsset = {
                content,
            };
            if (purpose) {
                asset.purpose = purpose;
            }
            onChange(assetId, asset);
        }
    }, [assetId, content, purpose, onChange]);

    useUpdateEffect(() => {
        setText((prev) => ({ ...prev, purpose: purposeSelector.value }));
    }, [purposeSelector]);

    return (
        <Control spacing="compact">
            <TextField
                label="Content"
                required
                value={content}
                status={isContentDirty && isStringEmptyOrWhitespace(content) ? 'error' : undefined}
                onChange={(e) => setText((prev) => ({ ...prev, content: e.target.value }))}
                onInput={() => setIsContentDirty(true)}
                onBlur={() => setIsContentDirty(true)}
                rightAddon={
                    <Button
                        color="danger"
                        variant="primary"
                        icon={<IconRemove className="icon-addon" />}
                        aria-label="Remove text asset"
                        onClick={() => onRemove(assetId)}
                    />
                }
            />

            <Select
                label="Purpose"
                value={purposeSelector}
                onChange={setPurposeSelector}
                options={TEXT_PURPOSE_SELECTOR_OPTIONS}
            />
        </Control>
    );
};
